import { useFormContext } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { Prompt, useHistory } from 'react-router';
import { Location } from 'history';
import { useCampaignContext, useUnsavedPromptContext } from '../../context';
import { UnsavedChangesModal } from '../../modals/UnsavedChangesModal';
import { useSaveCampaign } from '../../hooks/useSaveCampaign';
import { CampaignEditorFormState } from '../../types';

export const UnsavedChangesPrompt = () => {
  const {
    getValues,
    reset,
    formState: { isDirty, isValid },
  } = useFormContext();

  const {
    setPromptState,
    promptState: { openModal, consumerCallback },
  } = useUnsavedPromptContext();
  const history = useHistory();
  const { saveCampaign, isLoading } = useSaveCampaign();

  const { refetch, isLoading: isCampaignLoading } = useCampaignContext();

  const [nextLocation, setNextLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  useEffect(() => {
    if (!isDirty) {
      if (consumerCallback) {
        consumerCallback();
      }
      setPromptState({ openModal: false });
    }
  }, [consumerCallback, isDirty]);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      event.preventDefault();

      event.returnValue = '';
    },
    [isDirty],
  );

  // prompt for browser changes
  useEffect(() => {
    if (!isDirty) {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      return;
    }
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // prompt for react-router changes
  const promptForUnsavedChanges = (nextLoc: Location): boolean => {
    if (isDirty && !confirmedNavigation) {
      setNextLocation(nextLoc);
      setPromptState({
        openModal: true,
        consumerCallback: () => setConfirmedNavigation(true),
      });

      return false; // block the navigation
    } else {
      return true; // allow the navigation
    }
  };

  useEffect(() => {
    if (confirmedNavigation && nextLocation && !openModal) {
      // navigate to the intended blocked location
      history.push(nextLocation as unknown as string);
    }
  }, [confirmedNavigation, nextLocation, openModal]);

  const closeModal = () => {
    setPromptState({ openModal: false });
  };

  const onSave = () => {
    // async logic stuff goes
    saveCampaign(getValues() as CampaignEditorFormState, () => {
      if (consumerCallback) {
        if (!confirmedNavigation) {
          refetch();
        }
        consumerCallback();
      }
    });

    closeModal();
  };

  const onDiscardChanges = () => {
    window.removeEventListener('beforeunload', handleBeforeUnload); // remove the listener so if we're navigating away, we don't get prompted again

    reset();
    if (consumerCallback) {
      consumerCallback();
    }
    closeModal();
  };

  const onCancel = () => {
    closeModal();
  };

  return (
    <>
      <Prompt when={isDirty} message={promptForUnsavedChanges} />
      {!!openModal && isDirty && (
        <UnsavedChangesModal
          isLoading={isLoading || isCampaignLoading}
          isValid={isValid}
          onSave={onSave}
          onClose={onCancel}
          onDiscard={onDiscardChanges}
        />
      )}
    </>
  );
};
